import React, { useEffect, useId, useRef, useState } from 'react';
import BootstrapModal from 'bootstrap/js/dist/modal';
import CloseButton from '../button/CloseButton';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    header?: (titleId: string) => React.ReactNode;
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
}

/**
 * Real Bootstrap modal (backdrop, focus trap, ESC-to-close, scroll lock) driving the
 * uikit's `.modal`/`.modal-dialog`/`.modal-content` markup from televeda-bootstrap.
 *
 * The element is always mounted and shown/hidden imperatively via Bootstrap's JS API,
 * so Bootstrap owns the transition and cleanup rather than React unmounting mid-animation.
 */
const Modal = ({ isOpen, onClose, title, header, size = 'sm', children }: ModalProps) => {
  const titleId = useId();
  const elRef = useRef<HTMLDivElement | null>(null);
  // Keep the latest onClose without re-registering the listener on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Whether Bootstrap currently considers the modal shown, so we only call hide()
  // (and so only wait for `hidden.bs.modal`) when a hide will actually happen.
  const isShownRef = useRef(false);
  // Drives the contents, and lags `isOpen` by the length of Bootstrap's fade-out.
  const [renderChildren, setRenderChildren] = useState(false);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const instance = BootstrapModal.getOrCreateInstance(el);
    // Fires for every close path (backdrop click, ESC, close button) so React state
    // stays in sync with what Bootstrap actually did.
    const handleHidden = () => {
      isShownRef.current = false;
      setRenderChildren(false);
      onCloseRef.current();
    };
    el.addEventListener('hidden.bs.modal', handleHidden);

    return () => {
      el.removeEventListener('hidden.bs.modal', handleHidden);
      instance.dispose();
    };
  }, []);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const instance = BootstrapModal.getOrCreateInstance(el);
    if (isOpen) {
      setRenderChildren(true);
      isShownRef.current = true;
      instance.show();
    } else if (isShownRef.current) {
      // Leave the contents mounted; `hidden.bs.modal` tears them down once the
      // fade-out has finished (see the note on `renderChildren` below).
      instance.hide();
    } else {
      setRenderChildren(false);
    }
  }, [isOpen]);

  return (
    <div
      ref={elRef}
      className={`modal ${size}-modal fade`}
      role='dialog'
      aria-modal='true'
      aria-labelledby={title || header ? titleId : undefined}
    >
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          {header &&
            <div className='modal-header'>
              {header(titleId)}
              <CloseButton onClick={onClose} />
            </div>
          }
          {!header && title &&
            <div className='modal-header modal-header-basic'>
              <h2 id={titleId} className='size-h2 u-text-bold'>{title}</h2>
              <CloseButton onClick={onClose} />
            </div>
          }
          <div className='modal-body'>
            {/*
              Children are only mounted while open. The modal element itself stays mounted so
              Bootstrap owns the show/hide transition, but keeping the contents out of the DOM
              while closed avoids a closed modal's form fields registering with (and blocking
              validation of) a screen's react-hook-form instance.

              This tracks `renderChildren` rather than `isOpen` so the contents survive the
              fade-out: unmounting them the instant `isOpen` flips false emptied the body
              mid-transition, flashing a header-only box for the length of the fade.
            */}
            {renderChildren && children}
          </div>
        </div>
      </div>
    </div>
  )
};

export default Modal
